import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Image from 'next/image';
import Head from 'next/head';

import { Dropdown, Button } from 'app/seedly-component-library';
import { MoreHorizontal } from 'react-feather';
import UserPassport from 'app/components/profile/UserPassport';
import ProductLinkList from 'app/components/product/ProductLinkList';
import CommentList from 'app/components/comment/CommentList';
import PostCommentForm from 'app/components/comment/PostCommentForm';
import { generateOpinionLink } from 'app/utils/generateShareLink';
import { usePromptGroupModal } from 'entity/group/groupHooks';
import mixpanel, { trackedEvents } from 'utils/mixpanel';
import { SHARE_TYPE } from 'app/constants/app';
import BookmarkButton from 'components/post/BookmarkButton';

import { updateOpinionPageView } from 'app/dispatcher/opinionDispatcher';

import styled from 'styled-components';
import { imageUrl } from 'theme';
import useOnScreen from 'utils/hooks/useOnScreen';
import useIsScrolling from 'utils/hooks/useIsScrolling';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import useOpinionMenu from 'app/components/opinion/OpinionItem/hooks/useOpinionMenu';
import AdSlot from 'app/components/misc/AdSlot';
import getAuthHeaders from 'utils/getAuthHeaders';
import { openLoginModal, openShareModal } from 'dispatcher/modalDispatcher';
import { useRouter } from 'next/router';
import { SAVE_TYPE } from 'constants/app';
import AuthorInfo from './AuthorInfo';
import EngagementBar from './EngagementBar';
import OpinionContentMarkdown from './OpinionContentMarkdown';
import CommentButton from '../../components/blog/article/CommentButton';
import OpinionCommentOverlay from '../../components/blog/article/CommentOverlay/OpinionComment';

// NOTE: manually add in img css for next/img as the styling was missing.
// It cause the image to be thumbnail sized and its weird because it only happen in staging/prod
// This fix add in the styling manually.
const ImageWrapper = styled.div`
  width: 100%;

  img {
    position: absolute;

    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;

    box-sizing: border-box;
    padding: 0px;
    border: none;
    margin: auto;
    display: block;
    width: 0px;
    height: 0px;
    min-width: 100%;
    max-width: 100%;
    min-height: 100%;
    max-height: 100%;
  }
`;

const OpinionDetailContent = ({ opinion }) => {
  const dispatch = useDispatch();

  const {
    id,
    slug,
    image,
    title,
    description,
    post,
    user,
    createdAt,
    updatedAt,
    credential,
    content,
    createdByCurrentUser,
    published,
    parentCommentsCount,
    commentsCount,
  } = opinion;
  const scrollToComponent = useRef(null);
  const commentsSection = useRef(null);

  const router = useRouter();
  const { query } = router;
  const { openlogin } = query;
  const authHeader = getAuthHeaders();

  const [isCommentOpen, setCommentOpen] = useState(false);
  const commentSectionRef = useRef(null);
  const { isOnScreen, isAboveScreen } = useOnScreen(commentSectionRef);
  const isScrolling = useIsScrolling();

  useEffect(() => {
    scrollToComponent.current = require('react-scroll-to-component');
  }, []);

  useEffect(() => {
    dispatch(updateOpinionPageView(id));
  }, []);

  useEffect(() => {
    if (openlogin && openlogin === 'true' && isEmpty(authHeader)) {
      dispatch(openLoginModal());
    }
  }, []);

  // prompt user join group modal if logged in but not onboarded
  usePromptGroupModal();

  const handleScrollToComments = () => {
    scrollToComponent.current(commentsSection.current);
  };

  const menuOptions = useOpinionMenu({ opinion });

  const showCommentButton = !isOnScreen && !isAboveScreen && !isScrolling;

  return (
    <>
      {!published && (
        <Head>
          <meta name="robots" content="none, noarchive" />
        </Head>
      )}
      <div className="flex flex-col w-full lg:mt-3 mt-0 ">
        <AdSlot type="opinion" slotKey="leaderboard" />
        <div className="flex w-full justify-center">
          <div className="flex flex-col items-center w-full lg:w-[660px] gap-6 relative">
            <div className="flex flex-col w-full justify-center">
              {image && (
                <ImageWrapper>
                  <Image
                    alt="cover-image"
                    src={image}
                    layout="responsive"
                    height={346.5}
                    width={660}
                    priority
                  />
                </ImageWrapper>
              )}
              <div className="bg-white h-fit p-4 rounded">
                <div className="flex flex-col w-full gap-4">
                  <div className="flex w-full justify-between">
                    <div className="flex gap-2 items-center">
                      <img alt="cover" src={imageUrl.opinion} />
                      <p className="text-sm font-bold" variant="titlemd">
                        OPINIONS
                      </p>
                    </div>
                    {createdByCurrentUser && (
                      <Dropdown
                        items={menuOptions}
                        handleSelectedItemchange={item => {
                          item.selectedItem.onClick();
                        }}
                        button={
                          <Button variant="ghost" icon={<MoreHorizontal />} />
                        }
                      />
                    )}
                  </div>
                  <h1 className="text-[32px] font-bold">{title}</h1>
                  <p>{description}</p>
                  <ProductLinkList
                    productTags={get(post, 'productsList', [])}
                  />
                  <div className="horizontal-divider" />
                  <UserPassport
                    profile={user}
                    createdAt={createdAt}
                    updatedAt={updatedAt}
                    credential={credential || user.defaultCredential}
                  />
                  <div className="horizontal-divider" />
                  <OpinionContentMarkdown source={content} />
                  <div className="horizontal-divider" />
                  <p
                    className="text-neutral-500 font-bold text-xs"
                    variant="titlesm"
                    intent="secondary"
                  >
                    Comments
                  </p>
                  <div className="w-full" ref={commentsSection}>
                    <PostCommentForm
                      commentableType="Opinion"
                      commentableId={id}
                    />
                  </div>
                  <CommentList
                    totalCommentCount={parentCommentsCount}
                    commentableId={id}
                    commentableType="Opinion"
                    childCommentableType="OpinionComment"
                    commentShareLink={generateOpinionLink(slug)}
                  />
                </div>
              </div>
              <div
                className="mobile-only bg-white w-full h-fit p-4 fixed bottom-0 z-10 shadow-opinionBottomBar"
                width="100%"
                height="fit-content"
                paddingSpacing="md"
              >
                <EngagementBar
                  opinion={opinion}
                  onCommentClick={handleScrollToComments}
                />
              </div>
            </div>
            {showCommentButton && (
              <CommentButton
                handleCommentOpen={() => setCommentOpen(true)}
                commentsCount={commentsCount}
                onShareClick={() => {
                  mixpanel.track(trackedEvents.BlogCommentButtonClicked, {
                    param: 'share',
                  });
                  dispatch(
                    openShareModal(SHARE_TYPE.OPINION, '', null, {
                      imageUrl: opinion.image,
                      title: opinion.title,
                    }),
                  );
                }}
                bookmarkButton={
                  <BookmarkButton
                    contentId={opinion.id}
                    slug={opinion.slug}
                    type={SAVE_TYPE.OPINION}
                    onClickTrack={() => {
                      mixpanel.track(trackedEvents.BlogCommentButtonClicked, {
                        param: 'bookmark',
                      });
                    }}
                  />
                }
              />
            )}
          </div>
          <div className="desktop-only flex flex-col gap-4 ml-3 w-[352px]">
            <div className="p-4 bg-white w-full h-fit rounded sticky top-[79px]">
              <div className="flex flex-col gap-4 w-full">
                <AuthorInfo opinion={opinion} />
                <EngagementBar
                  opinion={opinion}
                  onCommentClick={handleScrollToComments}
                />
              </div>
            </div>
          </div>
        </div>
        <div ref={commentSectionRef} />
        <AdSlot type="opinion" slotKey="bottom-leaderboard" />
        <OpinionCommentOverlay
          isCommentOpen={isCommentOpen}
          handleCommentClose={() => setCommentOpen(false)}
          opinion={opinion}
        />
      </div>
    </>
  );
};

export default OpinionDetailContent;
